import React, { useEffect, useRef, useState } from "react";
import {
  checkPermissionStatus,
  generateMaskedPII,
  useDebounce,
} from "helpers/utils";
import { useTranslation } from "react-i18next";
import {
  DeleteOrgSPOC,
  FetchAllOrgSPOCs,
} from "services/OrganizationSPOCServices";
import { decryptDataFromArray } from "helpers/EncDec";
import { Col, Container, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import RemotePagination from "components/Common/RemotePagination";

import serach_icon from "../../../assets/images/icon/search-icon.svg";
import view_icon from "../../../assets/images/icon/eye_icon.svg";
import delete_icon from "../../../assets/images/icon/delete.svg";
import edit_icon from "../../../assets/images/icon/edit.svg";
import right_arrow from "../../../assets/images/icon/right-arrow-table.svg";
import arrow_btn from "../../../assets/images/icon/arrow-btn.svg";
import CreateSPOCModal from "./CreateSPOCModal";
import moment from "moment";
import EditSPOCModal from "./EditSPOCModal";
import Swal from "sweetalert2";

const SPOC = ({ orgDetails, ...props }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [spocList, setSPOCList] = useState([]);
  const [totalSPOC, setTotalSPOC] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [queryText, setQueryText] = useState("");
  const debouncedQueryText = useDebounce(queryText, 500);

  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editSPOCDetails, setEditSPOCDetails] = useState(null);

  const fetchData = async ({
    _pageNo = pageNo,
    _pageSize = pageSize,
    _queryText = queryText,
  }) => {
    try {
      setLoading(true);

      let reqBody = {
        organization_id: orgDetails?.id,
        queryText: _queryText,
        pageNo: _pageNo,
        pageSize: _pageSize,
      };

      let response = await FetchAllOrgSPOCs(reqBody);

      let responseData = decryptDataFromArray(response);

      setSPOCList(responseData?.spocList || []);
      setTotalSPOC(responseData?.total);
      setLoading(false);
    } catch (error) {
      toast.error(t(`Organization.SPOC.No SPOCs Available`));
      setSPOCList([]);
      setTotalSPOC(0);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      _queryText: debouncedQueryText,
    });

    return () => {};
  }, [debouncedQueryText]);

  const handleDeleteSPOC = async spocId => {
    try {
      let response = await DeleteOrgSPOC(spocId);

      let responseMsg = response?.data?.message || "SPOC Deleted Successfully";

      toast.success(responseMsg);

      fetchData({});
    } catch (error) {
      let errMsg =
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        "Error while deleting spoc";

      toast.error(errMsg);
    }
  };

  const columns = [
    {
      dataField: "name",
      text: t(`Organization.SPOC.Name`),
    },
    {
      dataField: "email_id",
      text: t(`Organization.SPOC.Email`),
      formatter: (email_id, row) => <>{generateMaskedPII(email_id)}</>,
    },
    {
      dataField: "mobile_no",
      text: t(`Organization.SPOC.Mobile`),
      formatter: (mobile_no, row) => <>{generateMaskedPII(mobile_no, 2)}</>,
    },
    {
      dataField: "role",
      text: t(`Organization.SPOC.Role`),
    },
    {
      dataField: "updated_at",
      text: t(`Organization.SPOC.Last Updated On`),
      classes: "white-hidden",
      formatter: (updated_at, row) => {
        return <>{moment(updated_at).format("DD MMM, YYYY")}</>;
      },
    },
  ];

  if (
    !!checkPermissionStatus("organization-spoc", "edit") ||
    !!checkPermissionStatus("organization-spoc", "delete")
  ) {
    columns.push({
      dataField: "id",
      text: t(`Organization.SPOC.Actions`),
      formatter: (spocId, spoc) => {
        return (
          <div className="d-flex gap-1 blue-icon-gap action-application">
            <div className="action-one small-img">
              {checkPermissionStatus("organization-spoc", "edit") ? (
                <>
                  <div
                    className="text-success blue-icon blue-color4"
                    role="button"
                    onClick={() => {
                      setEditSPOCDetails(spoc);
                      setEditModal(true);
                    }}
                  >
                    <img src={edit_icon} alt="edit_icon" />
                    <span>{t(`Organization.SPOC.Edit SPOC`)}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>

            <div className="action-one small-img">
              {checkPermissionStatus("organization-spoc", "delete") ? (
                <>
                  <div
                    className="text-success blue-icon blue-color3"
                    role="button"
                    onClick={e => {
                      Swal.fire({
                        title: t("Organization.SPOC.Are you sure?"),
                        text: t(
                          "Organization.SPOC.You won't be able to revert this"
                        ),
                        icon: "warning",
                        showCancelButton: true,
                        cancelButtonColor: "#d33",
                        cancelButtonText: t("Organization.SPOC.Cancel"),
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: t("Organization.SPOC.Delete"),
                        reverseButtons: true,
                      }).then(async result => {
                        if (!result.isConfirmed) {
                          return;
                        }

                        handleDeleteSPOC(spocId);
                      });
                    }}
                  >
                    <img src={delete_icon} alt="delete_icon" />
                    <span>{t(`Organization.SPOC.Delete SPOC`)}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    });
  }

  /**table arrow */
  const tableWrapperRef = useRef(null);

  const handleScroll = direction => {
    if (tableWrapperRef.current) {
      const scrollAmount = 100; // Adjust this value as needed
      tableWrapperRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setTimeout(() => {
      setPageNo(page);
      setPageSize(sizePerPage);
      fetchData({ _pageNo: page, _pageSize: sizePerPage });
    }, 500);
  };

  useEffect(() => {
    const theme = localStorage.getItem("company_theme");
    if (theme) {
      document.body.classList.add(`theme-${theme}`);
    }
  }, []);

  return (
    <>
      <div className="page-content departmentpage">
        <Container fluid className="p-0">
          <div className="joblist-container skilllist  applicationcard-table">
            <Row className="mb-3 align-items-center py-2 spece-low-cum2">
              <Col lg="4" md="4" className="Joblist-column">
                <h5 className="text-uppercase heading-one">
                  {t(`Organization.SPOC.SPOC List`)}
                </h5>
              </Col>
              <Col lg="8" md="8">
                <div class="single-filter-box skillset-card btn-left d-flex justify-content-end align-items-center gap-2 flex-wraps">
                  <div className="serach-bar-top">
                    <form class="form-inline">
                      <input
                        class="form-control mr-sm-2"
                        type="search"
                        placeholder={t(`Organization.SPOC.Search SPOC`)}
                        aria-label="Search"
                        onChange={e => {
                          let text = e.target?.value;

                          setQueryText(text);
                        }}
                        title={t(`Organization.SPOC.Search SPOC`)}
                      />
                      <span>
                        <img src={serach_icon} alt="search" />
                      </span>
                    </form>
                  </div>

                  {checkPermissionStatus("organization-spoc", "create") && (
                    <div
                      role="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setAddModal(true);
                      }}
                    >
                      {t(`Organization.SPOC.Create SPOC`)}
                    </div>
                  )}

                  <div className="tale-text right-panel skilltest">
                    <button
                      className="button left"
                      onClick={() => handleScroll("left")}
                      data-tooltip="Scroll Left"
                    >
                      <img src={right_arrow} alt="right-arrow" />
                    </button>
                    <button
                      className="button right"
                      onClick={() => handleScroll("right")}
                      data-tooltip="Scroll Right"
                    >
                      <img src={right_arrow} alt="right-arrow" />
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Row>
            <Col lg={12}>
              <div className="outerTab-box-wrapper below-vacancy  skillset-cards-po">
                <div className="timeto-hire-card userlist">
                  {!!loading && (
                    <div className="m-3 p-3 text-center">
                      <Spinner size={"lg"} />{" "}
                      <div>{t(`Organization.SPOC.Loading`)}</div>
                    </div>
                  )}

                  {!loading && (
                    <>
                      <div className="table-custome-class range-custome">
                        {!!spocList?.length ? (
                          <>
                            <div className="scroll-container">
                              <div
                                className="table-wrapper"
                                ref={tableWrapperRef}
                              >
                                <RemotePagination
                                  keyField="id"
                                  data={spocList}
                                  columns={columns}
                                  page={pageNo}
                                  sizePerPage={pageSize}
                                  totalSize={totalSPOC}
                                  onTableChange={handleTableChange}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <table className="table table-hover table-bordered">
                              <thead>
                                <tr>
                                  <th>{t(`Organization.SPOC.Name`)}</th>
                                  <th>{t(`Organization.SPOC.Email`)}</th>
                                  <th>{t(`Organization.SPOC.Mobile`)}</th>
                                  <th>{t(`Organization.SPOC.Role`)}</th>
                                  <th>
                                    {t(`Organization.SPOC.Last Updated On`)}
                                  </th>
                                  <th>{t(`Organization.SPOC.Actions`)}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    <div className="no-data data-available">
                                      <img
                                        src="../img/data-not-available.png"
                                        alt="data not availble"
                                      />
                                      <h3>
                                        {t(
                                          `Organization.SPOC.No SPOCs Available`
                                        )}
                                      </h3>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <CreateSPOCModal
          t={t}
          addModal={addModal}
          setAddModal={setAddModal}
          fetchData={fetchData}
          orgDetails={orgDetails}
        />

        <EditSPOCModal
          t={t}
          editModal={editModal}
          setEditModal={setEditModal}
          editSPOCDetails={editSPOCDetails}
          setEditSPOCDetails={setEditSPOCDetails}
          fetchData={fetchData}
        />
      </div>
    </>
  );
};

export default SPOC;
